import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Copy, Trash2, CalendarDays, Users2, Ban, TrafficCone, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EVENT_TYPES, STATUS } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NewEventDialog, DuplicateDialog } from "@/components/EventDialogs";
import { ConfirmDelete } from "@/components/ConfirmDelete";

const FinishCheck = ({ ev, canEdit, onToggle }) => {
  const done = ev.status === "finalizado";
  return (
    <button type="button" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); onToggle(ev, !done); }}
      title={done ? "Finalizado (pulse para reabrir)" : "Marcar como finalizado"} data-testid={`finish-event-checkbox-${ev.id}`} aria-pressed={done}
      className={`w-6 h-6 shrink-0 rounded-md border-2 grid place-items-center transition-colors ${done ? "bg-emerald-500 border-emerald-500 text-slate-950" : `border-slate-500 ${canEdit ? "hover:border-emerald-400" : ""}`} ${canEdit ? "cursor-pointer" : "cursor-default opacity-70"}`}>
      {done && <Check className="w-4 h-4" strokeWidth={3} />}
    </button>
  );
};

const Kpi = ({ icon: Icon, label, value, accent, testid, sub }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 rise" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-[0.625rem] tracking-widest text-slate-500 uppercase">{label}</span>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className="font-display text-3xl font-bold mt-3">{value}</div>
    {sub && <div className="text-[0.6875rem] text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { canEdit } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [dup, setDup] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => {
    api.get("/events").then((r) => setEvents(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, []);
  useEffect(() => {
    api.get("/stats", { params: year !== "all" ? { year } : {} }).then((r) => setStats(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [year, events]);

  const toggleFinish = async (ev, finished) => {
    try {
      const r = await api.patch(`/events/${ev.id}/finish`, { finished });
      setEvents((xs) => xs.map((x) => (x.id === ev.id ? { ...x, status: r.data.status } : x)));
      toast.success(finished ? "Evento finalizado: se descuenta del panel" : "Evento reabierto");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = useMemo(() => events.filter((e) => {
    if (year !== "all" && !e.date.startsWith(year)) return false;
    if (type !== "all" && e.type !== type) return false;
    if (status !== "all" && e.status !== status) return false;
    const s = q.toLowerCase();
    return !s || [e.name, e.location, e.description, e.created_by].some((x) => (x || "").toLowerCase().includes(s));
  }), [events, q, year, type, status]);

  const doDelete = async () => {
    try {
      await api.delete(`/events/${del.id}`);
      toast.success("Evento eliminado");
      setDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs tracking-[0.25em] text-sky-600 mb-2">ARCHIVO GENERAL</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Eventos y planes de tráfico</h1>
          <p className="text-slate-500 text-sm mt-2">Histórico de eventos organizados por la Policía Local.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setNewOpen(true)} className="h-11 px-5 font-semibold" data-testid="create-event-button">
            <Plus className="w-4 h-4 mr-1" /> Nuevo evento
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi icon={CalendarDays} label={year === "all" ? "Eventos totales" : `Eventos ${year}`} value={stats?.total_events ?? "–"} accent="text-sky-600" testid="kpi-total-events"
          sub={stats && `${stats.active_events} ${stats.active_events === 1 ? "activo" : "activos"} · ${stats.finished_events} ${stats.finished_events === 1 ? "finalizado" : "finalizados"}`} />
        <Kpi icon={Users2} label="Efectivos en eventos activos" value={stats?.total_agents ?? "–"} accent="text-amber-600" testid="kpi-total-agents" sub="No incluye eventos finalizados" />
        <Kpi icon={Ban} label="Cortes de tráfico activos" value={stats?.total_cuts ?? "–"} accent="text-red-600" testid="kpi-total-cuts" sub="No incluye eventos finalizados" />
        <Kpi icon={TrafficCone} label="Señales y vallas activas" value={stats?.total_signage ?? "–"} accent="text-emerald-600" testid="kpi-total-signage" sub="No incluye eventos finalizados" />
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por evento, calle, lugar u oficial…" className="pl-9 bg-white h-10" data-testid="event-search-input" />
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-[170px] bg-white" data-testid="event-year-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los años</SelectItem>
            {(stats?.years || []).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-44 bg-white" data-testid="event-type-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {Object.entries(EVENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid="event-status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="events-table">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 font-mono text-[0.625rem] tracking-widest text-slate-500 uppercase border-b border-slate-200">
          <div className="col-span-4">Fin · Evento</div><div className="col-span-2">Fecha</div><div className="col-span-2">Tipo</div>
          <div className="col-span-2">Elementos</div><div className="col-span-2 text-right">Acciones</div>
        </div>
        {filtered.length === 0 && <div className="p-10 text-center text-slate-500 text-sm" data-testid="events-empty">No hay eventos que coincidan.</div>}
        {filtered.map((e, i) => (
          <div key={e.id} onClick={() => nav(`/events/${e.id}`)} data-testid={`event-row-${e.id}`}
            className={`grid grid-cols-1 md:grid-cols-12 gap-2 px-5 py-4 border-b border-slate-200 hover:bg-slate-100 cursor-pointer transition-[background-color,opacity] items-center rise ${e.status === "finalizado" ? "opacity-50 hover:opacity-80" : ""}`}
            style={{ animationDelay: `${i * 30}ms` }}>
            <div className="md:col-span-4 flex items-center gap-3 min-w-0">
              <FinishCheck ev={e} canEdit={canEdit} onToggle={toggleFinish} />
              <div className="min-w-0">
                <div className={`font-semibold text-slate-900 ${e.status === "finalizado" ? "line-through decoration-emerald-400/60" : ""}`}>{e.name}</div>
                <div className="text-xs text-slate-500 truncate">{e.location || "—"}</div>
              </div>
            </div>
            <div className="md:col-span-2 font-mono text-xs text-slate-700">{e.date} · {e.start_time}</div>
            <div className="md:col-span-2 flex items-center gap-2">
              <span className="text-xs text-slate-700">{EVENT_TYPES[e.type] || e.type}</span>
              <span className={`text-[0.625rem] px-2 py-0.5 rounded-full border ${STATUS[e.status]?.cls}`}>{STATUS[e.status]?.label}</span>
            </div>
            <div className="md:col-span-2 font-mono text-[0.6875rem] text-slate-500">
              {e.counts.cut_point + e.counts.street_cut} cortes · {e.counts.no_parking} zonas
              {e.plan_summary && <div className="text-emerald-600">Plan: {e.plan_summary.agents_used} efectivos</div>}
            </div>
            <div className="md:col-span-2 flex justify-end gap-1" onClick={(ev) => ev.stopPropagation()}>
              {canEdit && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setDup(e)} data-testid={`duplicate-event-button-${e.id}`} title="Duplicar">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="hover:text-red-700" onClick={() => setDel(e)} data-testid={`delete-event-button-${e.id}`} title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <NewEventDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(ev) => nav(`/events/${ev.id}`)} />
      <DuplicateDialog source={dup} onClose={() => setDup(null)} onDone={(ev) => nav(`/events/${ev.id}`)} />
      <ConfirmDelete open={!!del} title={`¿Eliminar "${del?.name}"?`} onCancel={() => setDel(null)} onConfirm={doDelete} />
    </div>
  );
}
